import { useState } from 'react';

import { getSessionHeaders } from '../../lib/serverSession';
interface ManualRecordEditorProps {
  record: any;
  table: string;
  recordId: string;
  onClose: () => void;
  onSave: (updatedRecord: any) => void;
  onDelete: (recordId: string) => void;
}

export function ManualRecordEditor({
  record,
  table,
  recordId,
  onClose,
  onSave,
  onDelete
}: ManualRecordEditorProps) {
  const [editedRecord, setEditedRecord] = useState(record);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleFieldChange = (field: string, value: any) => {
    setEditedRecord({
      ...editedRecord,
      [field]: value
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const adminUserId = typeof (window as any).currentUserId === 'number' ? (window as any).currentUserId : null;
      if (!adminUserId) {
        throw new Error('Current admin user is not available. Please re-open the admin dashboard and try again.');
      }

      const response = await fetch('/api/admin/manual-record-editor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'UPDATE',
          table,
          recordId,
          record: editedRecord,
          userId: adminUserId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save record');
      }

      const data = await response.json();
      setSuccess('✅ Record saved successfully');
      onSave(data.data);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(`❌ Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError('');

      const adminUserId = typeof (window as any).currentUserId === 'number' ? (window as any).currentUserId : null;
      if (!adminUserId) {
        throw new Error('Current admin user is not available. Please re-open the admin dashboard and try again.');
      }

      const response = await fetch('/api/admin/manual-record-editor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'DELETE',
          table,
          recordId,
          record: editedRecord,
          userId: adminUserId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete record');
      }

      setSuccess('✅ Record deleted successfully');
      onDelete(recordId);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(`❌ Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getFieldType = (field: string, value: any): string => {
    // Check value type first for more accurate detection
    if (typeof value === 'boolean') return 'checkbox';
    if (typeof value === 'number') return 'number';

    // Check for specific numeric fields
    if (field.includes('amount') || field.includes('commission_rate') || field.includes('price') || field.includes('rate')) return 'number';

    // Check for datetime fields - be specific to avoid matching 'commission_rate'
    if (field.endsWith('_at') || field.endsWith('_date') || field === 'created_at' || field === 'updated_at') return 'datetime-local';

    // Check for ID fields that are numeric
    if (field.includes('id') && typeof value === 'number') return 'number';

    return 'text';
  };

  const editableFields = Object.keys(editedRecord).filter(
    key => !key.includes('created_at') && !key.includes('updated_at') && key !== 'id'
  );

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#1F2937',
        border: '1px solid #374151',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#FFFFFF', margin: 0 }}>
            ✏️ Edit {table} Record
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0
            }}
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            backgroundColor: '#7F1D1D',
            color: '#FCA5A5',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#1F3A1F',
            color: '#86EFAC',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}

        {/* Form Fields */}
        <div style={{ marginBottom: '24px' }}>
          {editableFields.map((field) => (
            <div key={field} style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '6px',
                textTransform: 'uppercase'
              }}>
                {field.replace(/_/g, ' ')}
              </label>
              {getFieldType(field, editedRecord[field]) === 'checkbox' ? (
                <input
                  type="checkbox"
                  checked={editedRecord[field] || false}
                  onChange={(e) => handleFieldChange(field, e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
              ) : getFieldType(field, editedRecord[field]) === 'number' ? (
                <input
                  type="number"
                  step="0.01"
                  value={editedRecord[field] ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      handleFieldChange(field, '');
                      return;
                    }
                    const parsed = Number.parseFloat(raw);
                    handleFieldChange(field, Number.isFinite(parsed) ? parsed : '');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              ) : getFieldType(field, editedRecord[field]) === 'datetime-local' ? (
                <input
                  type="datetime-local"
                  value={editedRecord[field] ? String(editedRecord[field]).slice(0, 16) : ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={editedRecord[field] || ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={saving || deleting}
            style={{
              padding: '10px 20px',
              backgroundColor: '#374151',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: saving || deleting ? 0.5 : 1
            }}
          >
            Cancel
          </button>

          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving || deleting}
            style={{
              padding: '10px 20px',
              backgroundColor: '#7F1D1D',
              color: '#FCA5A5',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: saving || deleting ? 0.5 : 1
            }}
          >
            🗑️ Delete
          </button>

          <button
            onClick={handleSave}
            disabled={saving || deleting}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: saving || deleting ? 0.5 : 1
            }}
          >
            {saving ? '💾 Saving...' : '💾 Save'}
          </button>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#7F1D1D',
            border: '1px solid #DC2626',
            borderRadius: '6px'
          }}>
            <p style={{ color: '#FCA5A5', marginBottom: '12px' }}>
              ⚠️ Are you sure you want to delete this record? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#374151',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#DC2626',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  opacity: deleting ? 0.5 : 1
                }}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

